// src/components/ui/MagicCard.jsx
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardActions, 
  CardHeader, 
  CardMedia,
  styled,
  useTheme
} from '@mui/material';
import { keyframes } from '@mui/system';

// Animations are now defined at the theme level
// These definitions serve as fallbacks and references
// See theme/components.js for the token definitions

// Create a shimmer effect for borders
const borderShimmer = keyframes`
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 200% 0%;
  }
`;

// Create a subtle glow effect
const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 15px rgba(123, 104, 238, 0.4);
  }
  50% {
    box-shadow: 0 0 25px rgba(123, 104, 238, 0.6);
  }
`;

// Base styled card
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'magical' && prop !== 'level' && prop !== 'cardColor',
})(({ theme, magical, level, cardColor }) => {
  // Get theme tokens
  const tokens = theme.aghrba?.tokens;
  
  // Define colors based on theme and props
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;
  const accentColor = theme.palette.accent?.main || '#FFD700';
  
  // Determine border color based on cardColor prop
  let borderColor = primaryColor;
  if (cardColor === 'secondary') borderColor = secondaryColor;
  if (cardColor === 'accent') borderColor = accentColor;
  
  return {
    position: 'relative',
    border: magical ? `1px solid transparent` : 'none',
    transition: tokens?.transitions?.standard || 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    backgroundColor: theme.palette.background.paper,
    borderRadius: tokens?.borderRadius?.card || 16,
    overflow: 'hidden',
    
    // Ensure all children are above the pseudo-elements
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
    
    // Different magical levels
    ...(magical && {
      // Level 1: Basic hover effect with subtle shadow
      ...(level === 1 && {
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: tokens?.shadows?.cardHover || `0 10px 30px rgba(0, 0, 0, 0.1)`,
        },
      }),
      
      // Level 2: Glow effect and more pronounced hover
      ...(level === 2 && {
        '&:hover': {
          transform: 'translateY(-10px) scale(1.02)',
          boxShadow: tokens?.shadows?.cardHoverMedium || `0 15px 35px rgba(123, 104, 238, 0.15)`,
        },
        // Use the theme's gradientBorder effect
        ...(tokens?.effects?.gradientBorder 
          ? tokens?.effects?.gradientBorder(theme, borderColor, secondaryColor)
          : {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 'inherit',
              padding: '2px',
              background: `linear-gradient(90deg, ${borderColor}, ${secondaryColor}, ${borderColor})`,
              backgroundSize: '200% 100%',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'exclude',
              animation: tokens?.animations?.gradient || `${borderShimmer} 3s linear infinite`,
              pointerEvents: 'none', // IMPORTANT: Allow pointer events to pass through
              zIndex: 0, // Ensure it stays below content
            }
          }
        )
      }),
      
      // Level 3: Full magical effect with animation
      ...(level === 3 && {
        animation: tokens?.animations?.glow || `${glow} 3s infinite ease-in-out`,
        '&:hover': {
          transform: 'translateY(-12px) scale(1.03)',
          boxShadow: tokens?.shadows?.cardHoverLarge || `0 20px 40px rgba(123, 104, 238, 0.3)`,
        },
        // Use the theme's magical border effect if available
        ...(!tokens?.effects?.magicalBorder ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 'inherit',
            padding: '2px',
            background: theme?.aghrba?.gradients?.cardBorder || `linear-gradient(90deg, ${borderColor}, ${secondaryColor}, ${accentColor}, ${borderColor})`,
            backgroundSize: '300% 100%',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            animation: tokens?.animations?.shine || `${borderShimmer} 5s linear infinite`,
            pointerEvents: 'none', // IMPORTANT: Allow pointer events to pass through
            zIndex: 0, // Ensure it stays below content
          }
        } : {}),
      }),
    }),
  };
});

// The MagicCard component
const MagicCard = React.forwardRef(({
  children,
  magical = false,
  level = 1,
  cardColor = 'primary',
  title,
  subheader,
  avatar,
  action,
  image,
  imageHeight,
  actions,
  contentProps,
  headerProps,
  ...props
}, ref) => {
  const theme = useTheme();
  const [raised, setRaised] = useState(false);
  
  // Handle hover events
  const handleMouseOver = () => setRaised(true);
  const handleMouseOut = () => setRaised(false);
  
  return (
    <StyledCard
      ref={ref}
      magical={magical}
      level={level}
      cardColor={cardColor}
      raised={raised || magical}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      {...props}
    >
      {/* Render header if title is provided */}
      {title && (
        <CardHeader
          title={title}
          subheader={subheader}
          avatar={avatar}
          action={action}
          {...headerProps}
        />
      )}
      
      {/* Render image if provided */}
      {image && (
        <CardMedia
          component="img"
          height={imageHeight || 140}
          image={image}
          alt={title || 'Card image'}
        />
      )}
      
      {/* Card content */}
      <CardContent 
        sx={{ position: 'relative', zIndex: 1 }} // Ensure content stays above the pseudo-element
        {...contentProps}
      >
        {children}
      </CardContent>
      
      {/* Render actions if provided */}
      {actions && (
        <CardActions>
          {actions}
        </CardActions>
      )}
    </StyledCard>
  );
});

MagicCard.displayName = 'MagicCard';

export default MagicCard;